import { useState } from 'react';

import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';

import Button from '@zenbusiness/zui/Button';
import Text from '@zenbusiness/zui/Text';

import { CLICKSTREAM_COMMON_PROPS } from '#client/components/utils/constants';
import { reportMarkdownComponents } from '#client/components/utils/markdownComponents';

import * as Styled from './ProfilePanel.style';

/**
 * Generic profile panel component for displaying key-value pairs and optional actions
 * Consolidates UserProfilePanel and similar profile display components
 *
 * @param {object} props - Component props
 * @param {Array} props.fields - Array of field definitions
 * @param {string} props.fields[].label - Field label
 * @param {string|React.ReactNode} props.fields[].value - Field value (string or React node)
 * @param {Function} [props.fields[].render] - Custom render function for the value (optional)
 * @param {boolean} [props.fields[].useMarkdown=false] - Whether to render value as markdown
 * @param {Function} [props.onStartOver] - Handler for start over action (optional, hides button if not provided)
 * @param {string} [props.startOverText='Start over'] - Text for start over button
 * @param {string} [props.clickstreamElementName='profile_start_over'] - Clickstream element name
 * @param {boolean} [props.loading=false] - Whether action is in progress
 * @component
 */
function ProfilePanel({
  fields = [],
  onStartOver,
  startOverText = 'Start over',
  clickstreamElementName = 'profile_start_over',
  loading = false
}) {
  const [startOverLoading, setStartOverLoading] = useState(false);

  const handleStartOver = async () => {
    if (startOverLoading || loading || !onStartOver) {
      return;
    }

    setStartOverLoading(true);
    try {
      await onStartOver();
    } catch (error) {
      console.error('Failed to start over:', error);
    } finally {
      setStartOverLoading(false);
    }
  };

  const renderFieldValue = (field) => {
    // Custom render function takes precedence
    if (field.render) {
      return field.render(field.value);
    }

    // Markdown rendering
    if (field.useMarkdown && typeof field.value === 'string') {
      return (
        <Text style={{ fontWeight: '600' }}>
          <ReactMarkdown components={reportMarkdownComponents}>{field.value}</ReactMarkdown>
        </Text>
      );
    }

    // React node (already rendered)
    if (typeof field.value !== 'string') {
      return field.value;
    }

    // Default: plain text
    return (
      <Styled.ProfileFieldValue size="md" weight="medium">
        {field.value || '—'}
      </Styled.ProfileFieldValue>
    );
  };

  return (
    <>
      <Styled.ProfileSection>
        {fields.map((field, index) => {
          // Skip fields with falsy values (unless explicitly rendered)
          if (!field.value && !field.render) {
            return null;
          }

          return (
            <Styled.ProfileField key={field.key || field.label || index}>
              <Styled.ProfileFieldLabel size="md">{field.label}</Styled.ProfileFieldLabel>
              {renderFieldValue(field)}
            </Styled.ProfileField>
          );
        })}
      </Styled.ProfileSection>

      {onStartOver && (
        <Styled.ButtonsContainer>
          <Button
            clickstreamProps={{
              ...CLICKSTREAM_COMMON_PROPS,
              elementName: clickstreamElementName,
              elementText: startOverText
            }}
            disabled={startOverLoading || loading}
            fullWidth
            loading={startOverLoading}
            size="medium"
            variant="outlined"
            onClick={handleStartOver}
          >
            {startOverText}
          </Button>
        </Styled.ButtonsContainer>
      )}
    </>
  );
}

ProfilePanel.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      render: PropTypes.func,
      useMarkdown: PropTypes.bool
    })
  ).isRequired,
  onStartOver: PropTypes.func,
  startOverText: PropTypes.string,
  clickstreamElementName: PropTypes.string,
  loading: PropTypes.bool
};

export default ProfilePanel;
